import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "./apiService";
import {
  UpdateProfileRequest,
  ReservationRequest,
  VehicleRequest,
} from "./ApiClient";
import { toast } from "react-toastify";
import { useAuth } from "../auth/authContext";
import { useEffect, useState } from "react";

// Custom hook to ensure API client is ready with auth token
const useApiReady = () => {
  const { token, currentUser, loading } = useAuth();
  const [isApiReady, setIsApiReady] = useState(false);

  useEffect(() => {
    if (token && currentUser && !loading) {
      // Small delay to ensure ApiClientProvider has set the token
      const timer = setTimeout(() => {
        setIsApiReady(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsApiReady(false);
    }
  }, [token, currentUser, loading]);

  return isApiReady;
};

// Query Keys
export const queryKeys = {
  profile: ["profile"] as const,
  sites: ["sites"] as const,
  vehicles: ["vehicles"] as const,
  reservations: ["reservations"] as const,
  transactions: (startDate?: string, endDate?: string) =>
    ["transactions", { startDate, endDate }] as const,
  config: ["config"] as const,
  invoice: (id: string) => ["invoice", id] as const,
  visitor: (id: string) => ["visitor", id] as const,
  paymentMethods: ["paymentMethods"] as const,
};

// Profile Hooks
export const useProfile = () => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getProfile();
      return response.data;
    },
    enabled: isApiReady, // Only run when API client is fully ready
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => apiService.updateProfile(data),
    onSuccess: (response) => {
      // Update the profile cache with the response
      queryClient.setQueryData(queryKeys.profile, response.data);
      toast.success("Profile updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to update profile: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

export const useReissueQrCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.reissueQrCode(),
    onSuccess: () => {
      // Invalidate profile cache to refetch the updated profile with new QR code
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      toast.success("QR Code reissued successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to reissue QR code: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

// Site Hooks
export const useSites = () => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.sites,
    queryFn: async () => {
      const response = await apiService.getSites();
      return response.data;
    },
    enabled: isApiReady, // Only run when API client is fully ready
    staleTime: 5 * 60 * 1000, // Sites data is relatively static, cache for 5 minutes
  });
};

// Vehicle Hooks
export const useVehicles = () => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.vehicles,
    queryFn: async () => {
      const response = await apiService.getVehicles();
      return response.data;
    },
    enabled: isApiReady, // Only run when API client is fully ready
  });
};
export const useCreateVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VehicleRequest) => apiService.createVehicle(data),
    onSuccess: () => {
      // Refresh vehicles list
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      toast.success("Vehicle created successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to create vehicle: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VehicleRequest }) =>
      apiService.updateVehicle(id, data),
    onSuccess: () => {
      // Refresh vehicles list
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      toast.success("Vehicle updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to update vehicle: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiService.deleteVehicle(id),
    onSuccess: () => {
      // Refresh vehicles list
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      toast.success("Vehicle deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to delete vehicle: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

// Reservation Hooks
export const useReservations = () => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.reservations,
    queryFn: async () => {
      const response = await apiService.getReservations();
      return response.data;
    },
    enabled: isApiReady, // Only run when API client is fully ready
  });
};

export const useCreateReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReservationRequest) =>
      apiService.createReservation(data),
    onSuccess: (response) => {
      // Check if the response contains an error
      if (response?.data?.error && response.data.error.trim() !== "") {
        // Don't show success toast if there's an error
        // The error will be handled by the component
        return;
      }

      // Only show success toast if there's no error
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations });
      toast.success("Reservation created successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to create reservation: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

export const useCalculateReservation = () => {
  return useMutation({
    mutationFn: (data: ReservationRequest) =>
      apiService.calculateReservation(data),
    onError: (error: any) => {
      toast.error(
        "Failed to calculate reservation: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

export const useCancelReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiService.cancelReservation(id),
    onSuccess: (response) => {
      // Check if the response contains an error
      if (response?.data?.error && response.data.error.trim() !== "") {
        // Don't show success toast if there's an error
        // The error will be handled by the component
        return;
      }

      // Only show success toast if there's no error
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations });
      toast.success("Reservation cancelled successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to cancel reservation: " +
          (error?.response?.data?.detail || error.message)
      );
    },
  });
};

// Transaction Hooks
export const useTransactions = (startDate?: string, endDate?: string) => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.transactions(startDate, endDate),
    queryFn: async () => {
      const response = await apiService.getTransactions({ startDate, endDate });
      return response.data;
    },
    enabled: isApiReady, // Only run when API client is fully ready
  });
};

// Config Hooks
export const useConfig = () => {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: async () => {
      const response = await apiService.getConfig();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // Config is very static, cache for 10 minutes
  });
};

// Invoice Hooks
export const useInvoice = (id: string) => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.invoice(id),
    queryFn: async () => {
      const response = await apiService.getInvoice(id);
      return response.data;
    },
    enabled: !!id && isApiReady, // Only run if id is provided and API client is ready
  });
};

// Visitor Hooks
export const useVisitor = (id: string) => {
  const isApiReady = true; // assume ready, as we don't need auth

  return useQuery({
    queryKey: queryKeys.visitor(id),
    queryFn: async () => {
      const response = await apiService.getVisitor(id);
      return response;
    },
    enabled: !!id && isApiReady, // Only run if id is provided and API client is ready
  });
};

// Payment Methods Hooks
export const usePaymentMethods = () => {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: queryKeys.paymentMethods,
    queryFn: async () => {
      const response = await apiService.getPaymentMethods();
      return response.data;
    },
    enabled: isApiReady,
  });
};

export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      apiService.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods });
      toast.success("Default payment method updated");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to set default payment method: " +
          (error?.response?.data?.error || error.message)
      );
    },
  });
};

export const useRemovePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      apiService.removePaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods });
      toast.success("Payment method removed successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to remove payment method: " +
          (error?.response?.data?.error || error.message)
      );
    },
  });
};

export const useCreateSetupIntent = () => {
  return useMutation({
    mutationFn: (apiVersion?: string) => apiService.createSetupIntent(apiVersion),
    onError: (error: any) => {
      toast.error(
        "Failed to initialize payment setup: " +
          (error?.response?.data?.error || error.message)
      );
    },
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.deleteAccount(),
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      toast.success("Account deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        "Failed to delete account: " +
          (error?.response?.data?.error || error.message)
      );
    },
  });
};

export const useCreateCustomerPortal = () => {
  return useMutation({
    mutationFn: (returnUrl?: string) => apiService.createCustomerPortal(returnUrl),
    onError: (error: any) => {
      toast.error(
        "Failed to open customer portal: " +
          (error?.response?.data?.error || error.message)
      );
    },
  });
};
